import { Outlet } from 'react-router';
import { useApolloClient } from '@apollo/client/react';
import { toast } from 'sonner';
import { LogOut, AlertTriangle, Sun, Moon } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@staff-app/ui';
import { useAuthStore } from '@/stores/auth-store';
import { useThemeStore, THEMES, THEME_LABELS, type ThemeName } from '@/stores/theme-store';
import { fullLogout } from '@/lib/logout';
import {
  useStaffGetActiveSessions,
  useStaffRestoreOwnAccount,
} from '@/features/customers/hooks/useStaffCustomer';
import { StaffSidebar } from './StaffSidebar';

function roleLabel(role: string): string {
  switch (role) {
    case 'root':
      return 'Root';
    case 'engineer':
      return 'Engineer';
    case 'support':
      return 'Support';
    case 'fulfillment':
      return 'Fulfillment';
    case 'billing':
      return 'Billing';
    case 'readonly':
      return 'Read only';
    default:
      return role;
  }
}

function ActiveSessionBanner() {
  const { data } = useStaffGetActiveSessions();
  const [restore, { loading }] = useStaffRestoreOwnAccount();

  const session = data?.staffGetActiveSessions?.[0];
  if (!session) return null;

  const handleReset = async () => {
    try {
      await restore();
      toast.success('Account restored');
    } catch (e: unknown) {
      toast.error('Reset failed', { description: (e as Error)?.message ?? 'Unknown error' });
    }
  };

  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-warning/40 bg-warning-bg px-6 py-2 text-xs text-warning">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span>
          {'You are logged in as '}
          <span className="font-semibold font-mono">{session.targetOwnerEmail}</span>
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="h-6 px-2 text-xs border-warning/60 bg-warning-bg text-warning hover:bg-warning/10"
        onClick={handleReset}
        disabled={loading}
      >
        {loading ? 'Resetting…' : 'Reset'}
      </Button>
    </div>
  );
}

export function StaffShell() {
  const client = useApolloClient();
  const adminUser = useAuthStore((s) => s.adminUser);
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const theme = useThemeStore((s) => s.theme);
  const mode = useThemeStore((s) => s.mode);
  const setTheme = useThemeStore((s) => s.setTheme);
  const setMode = useThemeStore((s) => s.setMode);

  const email = adminUser?.email ?? firebaseUser?.email ?? '';
  const roles = adminUser?.roles ?? [];
  const role = roles.map(roleLabel).join(' + ');

  const handleSignOut = async () => {
    await fullLogout(client);
  };

  return (
    <div className="flex h-screen flex-col bg-background" data-testid="staff-shell">
      {/* Top bar */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-6">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-foreground">{'OptiSigns Staff'}</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="cursor-pointer"
              data-testid="user-menu-trigger"
            >
              <span className="text-sm">{email}</span>
              {role ? (
                <span className="ml-2 rounded-sm bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                  {roleLabel(role)}
                </span>
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="truncate">{email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              {'Theme'}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => setTheme(value as ThemeName)}
            >
              {THEMES.map((name) => (
                <DropdownMenuRadioItem
                  key={name}
                  value={name}
                  className="cursor-pointer"
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`theme-option-${name}`}
                >
                  {THEME_LABELS[name]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={(e) => {
                e.preventDefault();
                setMode(mode === 'dark' ? 'light' : 'dark');
              }}
              data-testid="mode-toggle"
            >
              {mode === 'dark' ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Moon className="mr-2 h-4 w-4" />
              )}
              {mode === 'dark' ? 'Light mode' : 'Dark mode'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleSignOut}
              data-testid="sign-out-button"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {'Sign out'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <ActiveSessionBanner />

      {/* Body: sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        <StaffSidebar />
        <main className="flex-1 overflow-auto" data-testid="staff-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
